import React, { createContext, useEffect, useMemo } from 'react';
import { Conversation, ParticipantInfo } from '@marageti/z4-sdk/lib/travel';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { enqueueSnackbar } from 'notistack';
import PersonType from '@marageti/z4-sdk/lib/people/PersonType';
import { ProviderProps } from '../../types';
import { ValueProps } from './types';
import {
  setConversationsError,
  setConversationsLoading,
  setConversationsMap,
  setConversationAvatar,
  setUnreadCount,
} from './actions';
import useConversationsReducer from './use-conversations-reducer';

export const ConversationsStore = createContext({} as ValueProps);

export const UNREAD_COUNT_PARAMS = { hasNewOnly: true, limit: 0 };

export const ConversationsProvider = ({ children, showUnreadOnly, personId }: ProviderProps) => {
  const [conversationsState, conversationsDispatch] = useConversationsReducer();
  const apiClient = useApiClient();

  const params = useMemo(() => ({
    hasNewOnly: showUnreadOnly,
    page: 1,
    limit: 30,
    ...(personId != null && personId !== '' ? { person: personId } : {}),
  }), [showUnreadOnly, personId]);

  const unreadParams = useMemo(() => ({
    ...UNREAD_COUNT_PARAMS,
    ...(personId != null && personId !== '' ? { person: personId } : {}),
  }), [personId]);

  const {
    data,
    error,
    isLoading,
    mutate,
  } = useFetch<SearchResult<Conversation>>(apiClient.conversationsClient.getParticipatingConversations, [{ params }], true);
  const {
    data: unreadData,
    error: unreadError,
    isLoading: unreadIsLoading,
    mutate: unreadMutate,
  } = useFetch<SearchResult<Conversation>>(apiClient.conversationsClient.getParticipatingConversations, [{ params: unreadParams }], true);

  const refreshConversationList = () => {
    mutate();
    unreadMutate();
  };

  const readUnreadConfig = useMemo(
    () => (personId != null && personId !== '' ? { params: { person: personId } } : undefined),
    [personId],
  );

  const markConversationRead = (id: string) => {
    apiClient.conversationsClient.putConversationRead(id, readUnreadConfig)
      .then(() => {
        enqueueSnackbar('Marked conversation as read', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to mark conversation as read', { variant: 'default' });
      })
      .finally(() => {
        refreshConversationList();
      });
  };

  const markConversationUnread = (id: string) => {
    apiClient.conversationsClient.putConversationUnread(id, readUnreadConfig)
      .then(() => {
        enqueueSnackbar('Marked conversation as unread', { variant: 'success' });
      })
      .catch(() => {
        enqueueSnackbar('Failed to mark conversation as unread', { variant: 'default' });
      })
      .finally(() => {
        refreshConversationList();
      });
  };

  // Clear the list when filter changes (e.g. agent selection) so we don't show stale conversations
  useEffect(() => {
    conversationsDispatch(setConversationsMap({}));
  }, [personId, showUnreadOnly, conversationsDispatch]);

  useEffect(() => {
    if (data?.hits) {
      const conversationsMap = data.hits.reduce((acc, cS) => ({
        ...acc,
        [cS.id]: cS,
      }), {});
      conversationsDispatch(setConversationsMap(conversationsMap));
    }
    if (unreadData) {
      conversationsDispatch(setUnreadCount(unreadData.totalHits));
    }
  }, [data, unreadData]);

  useEffect(() => {
    conversationsDispatch(setConversationsLoading(isLoading || unreadIsLoading));
    conversationsDispatch(setConversationsError(error || unreadError));
  }, [isLoading, unreadIsLoading, error, unreadError]);

  useEffect(() => {
    if (data?.hits) {
      const conversationIds = data.hits.map((conversation) => conversation.id);
      const missingConversationIds = conversationIds.filter(
        (conversationId) => !(conversationId in conversationsState.avatarsByConversationId),
      );

      if (!missingConversationIds.length) {
        return;
      }

      const fetchAvatars = async () => {
        await Promise.all(missingConversationIds.map(async (conversationId) => {
          try {
            const participantsResponse = await apiClient.conversationsClient.getParticipants(
              conversationId,
              { params: { inactive: false } },
            );

            const participantsArray: ParticipantInfo[] = Array.isArray(participantsResponse)
              ? participantsResponse
              : Object.values(participantsResponse ?? {});

            const travelerAvatar = participantsArray.find(
              (participant) => participant.personType === PersonType.Traveler,
            )?.avatar ?? undefined;

            conversationsDispatch(setConversationAvatar(conversationId, travelerAvatar));
          } catch (err) {
            conversationsDispatch(setConversationAvatar(conversationId, undefined));
          }
        }));
      };

      fetchAvatars();
    }
  }, [apiClient, conversationsDispatch, conversationsState.avatarsByConversationId, data?.hits]);

  const value: ValueProps = useMemo(
    () => ({
      ...conversationsState,
      conversationsDispatch,
      markConversationRead,
      markConversationUnread,
      refreshConversationList,
      getTravelerAvatar: (conversationId: string) => conversationsState.avatarsByConversationId[conversationId] ?? undefined,
    }),
    [
      conversationsState,
      conversationsDispatch,
      markConversationRead,
      markConversationUnread,
      refreshConversationList,
    ],
  );

  return (
    <ConversationsStore.Provider value={value}>
      {children}
    </ConversationsStore.Provider>
  );
};
